import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../store/reducers';
import { getSumOpenInstallments, getSumOpenInstallmentsDetailed } from '../../../store/actions/installmentActions';
import {
    Box,
    Typography,
    Paper,
    CircularProgress,
    Grid,
    Card,
    CardContent,
    Button
} from '@mui/material';
import MUIDataTable, { MUIDataTableColumnDef } from 'mui-datatables';
import {
    ComposedChart,
    Bar,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
    BarChart
} from 'recharts';
import { format, parseISO, isValid, isBefore } from 'date-fns';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { exportToExcel } from '../../../utils/excelExport';

const CHART_COLORS = ['#1976d2', '#dc004e', '#ff9800', '#4caf50', '#9c27b0'];

const formatBRL = (value: number) =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);

const formatDate = (dateStr: string | null | undefined): string => {
    if (!dateStr) return '-';
    try {
        const parsed = parseISO(dateStr);
        return isValid(parsed) ? format(parsed, 'dd/MM/yyyy') : '-';
    } catch {
        return '-';
    }
};

const Dashboard: React.FC = () => {
    const dispatch = useDispatch();

    const sumOpen = useSelector((state: RootState) => state.installments.sumOpen);
    const sumOpenDetailed = useSelector((state: RootState) => state.installments.sumOpenDetailed);
    const isFetchingSummary = useSelector((state: RootState) => state.installments.isFetchingSummary);
    const isFetchingDetailed = useSelector((state: RootState) => state.installments.isFetchingDetailed);

    useEffect(() => {
        // @ts-ignore
        dispatch(getSumOpenInstallments());
        // @ts-ignore
        dispatch(getSumOpenInstallmentsDetailed());
    }, [dispatch]);

    // Process data for "Inadimplencia ate hoje" chart
    const overdueChartData = useMemo(() => {
        if (!Array.isArray(sumOpenDetailed)) return [];

        const now = new Date();
        const overdueByMonth: Record<string, { month: string; SaldoAberto: number; count: number }> = {};

        sumOpenDetailed.forEach((item: any) => {
            const dueDate = item.DueDate ? parseISO(item.DueDate) : null;
            if (!dueDate || !isValid(dueDate)) return;
            if (!isBefore(dueDate, now)) return;

            const saldo = (item.Amount || 0) - (item.AmountPaid || 0);
            if (saldo <= 0) return;

            const monthKey = format(dueDate, 'yyyy-MM');
            const monthLabel = format(dueDate, 'MM/yyyy');

            if (!overdueByMonth[monthKey]) {
                overdueByMonth[monthKey] = { month: monthLabel, SaldoAberto: 0, count: 0 };
            }
            overdueByMonth[monthKey].SaldoAberto += saldo;
            overdueByMonth[monthKey].count += 1;
        });

        return Object.entries(overdueByMonth)
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([, v]) => v);
    }, [sumOpenDetailed]);

    // Process data for "Previsao" chart
    const forecastChartData = useMemo(() => {
        if (!Array.isArray(sumOpenDetailed)) return [];

        const now = new Date();
        const futureByMonth: Record<string, { month: string; ValorPrevisto: number; count: number }> = {};

        sumOpenDetailed.forEach((item: any) => {
            const dueDate = item.DueDate ? parseISO(item.DueDate) : null;
            if (!dueDate || !isValid(dueDate)) return;
            if (isBefore(dueDate, now)) return;

            const saldo = (item.Amount || 0) - (item.AmountPaid || 0);
            if (saldo <= 0) return;

            const monthKey = format(dueDate, 'yyyy-MM');
            const monthLabel = format(dueDate, 'MM/yyyy');

            if (!futureByMonth[monthKey]) {
                futureByMonth[monthKey] = { month: monthLabel, ValorPrevisto: 0, count: 0 };
            }
            futureByMonth[monthKey].ValorPrevisto += saldo;
            futureByMonth[monthKey].count += 1;
        });

        return Object.entries(futureByMonth)
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([, v]) => v);
    }, [sumOpenDetailed]);

    // Delinquency table data
    const delinquencyTable = useMemo(() => {
        if (!Array.isArray(sumOpenDetailed)) return [];

        const now = new Date();
        const byPerson: Record<string, { Nome: string; Carne: string; Celular: string; Qtde: number; SaldoAberto: number }> = {};

        sumOpenDetailed.forEach((item: any) => {
            const dueDate = item.DueDate ? parseISO(item.DueDate) : null;
            if (!dueDate || !isValid(dueDate)) return;
            if (!isBefore(dueDate, now)) return;

            const saldo = (item.Amount || 0) - (item.AmountPaid || 0);
            if (saldo <= 0) return;

            const name = item.PersonName || item.Name || '';
            const carne = item.PaymentCardNumber || '-';
            const key = name || `Carnê ${carne}`;
            if (!byPerson[key]) {
                byPerson[key] = {
                    Nome: key,
                    Carne: carne,
                    Celular: item.MobileNumber || item.Phone || '-',
                    Qtde: 0,
                    SaldoAberto: 0
                };
            }
            byPerson[key].Qtde += 1;
            byPerson[key].SaldoAberto += saldo;
        });

        return Object.values(byPerson).sort((a, b) => b.SaldoAberto - a.SaldoAberto);
    }, [sumOpenDetailed]);

    // Summary calculations
    const totalOpenAmount = delinquencyTable.reduce((acc, row) => acc + row.SaldoAberto, 0);
    const totalOpenCount = delinquencyTable.reduce((acc, row) => acc + row.Qtde, 0);

    const handleExportDelinquency = () => {
        exportToExcel(
            delinquencyTable,
            [
                { key: 'Nome', label: 'Nome' },
                { key: 'Carne', label: 'Carnê' },
                { key: 'Celular', label: 'Celular' },
                { key: 'Qtde', label: 'Quantidade' },
                { key: 'SaldoAberto', label: 'Saldo em Aberto' },
            ],
            'inadimplencia'
        );
    };

    const delinquencyColumns: MUIDataTableColumnDef[] = [
        { name: 'Nome', label: 'Nome', options: { filter: true, sort: true } },
        { name: 'Carne', label: 'Carnê', options: { filter: true, sort: true } },
        { name: 'Celular', label: 'Celular', options: { filter: false, sort: true } },
        { name: 'Qtde', label: 'Qtde', options: { filter: false, sort: true } },
        {
            name: 'SaldoAberto',
            label: 'Saldo em Aberto',
            options: {
                filter: false,
                sort: true,
                customBodyRender: (value: number) => (
                    <Typography variant="body2" color="error" fontWeight="bold">
                        {formatBRL(value)}
                    </Typography>
                )
            }
        },
    ];

    const delinquencyTableOptions = {
        filterType: 'dropdown' as const,
        responsive: 'standard' as const,
        selectableRows: 'none' as const,
        rowsPerPage: 15,
        rowsPerPageOptions: [15, 30, 50, 100],
        download: false,
        print: false,
        textLabels: {
            body: {
                noMatch: 'Nenhum registro de inadimplência',
                toolTip: 'Ordenar',
            },
            pagination: {
                next: 'Próxima Página',
                previous: 'Página Anterior',
                rowsPerPage: 'Registros por página:',
                displayRows: 'de',
            },
            toolbar: {
                search: 'Pesquisar',
                viewColumns: 'Ver colunas',
                filterTable: 'Filtrar',
            },
            filter: {
                all: 'Todos',
                title: 'Filtros',
                reset: 'Reiniciar',
            },
        },
    };

    const isLoading = isFetchingSummary || isFetchingDetailed;

    const tooltipFormatter = (value: number) => formatBRL(value);

    return (
        <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h5" fontWeight="bold">Dashboard Financeiro</Typography>
                <Box>
                    <Button
                        variant="outlined"
                        startIcon={<FileDownloadIcon />}
                        onClick={handleExportDelinquency}
                        disabled={delinquencyTable.length === 0}
                    >
                        Exportar Inadimplência
                    </Button>
                </Box>
            </Box>

            {isLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                    <CircularProgress />
                </Box>
            ) : (
                <>
                    {/* Summary Cards */}
                    <Grid container spacing={2} sx={{ mb: 3 }}>
                        <Grid item xs={12} sm={4}>
                            <Card elevation={2}>
                                <CardContent sx={{ textAlign: 'center' }}>
                                    <Typography variant="body2" color="text.secondary">Total em Aberto</Typography>
                                    <Typography variant="h5" fontWeight="bold" color="error">
                                        {formatBRL(totalOpenAmount)}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <Card elevation={2}>
                                <CardContent sx={{ textAlign: 'center' }}>
                                    <Typography variant="body2" color="text.secondary">Quantidade de Parcelas</Typography>
                                    <Typography variant="h5" fontWeight="bold" color="primary">
                                        {totalOpenCount}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <Card elevation={2}>
                                <CardContent sx={{ textAlign: 'center' }}>
                                    <Typography variant="body2" color="text.secondary">Alunos Inadimplentes</Typography>
                                    <Typography variant="h5" fontWeight="bold" color="warning.main">
                                        {delinquencyTable.length}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>

                    {/* Charts */}
                    <Grid container spacing={3} sx={{ mb: 3 }}>
                        {/* Chart 1: Inadimplencia */}
                        <Grid item xs={12} md={6}>
                            <Paper elevation={2} sx={{ p: 2 }}>
                                <Typography variant="h6" gutterBottom>Inadimplência até hoje</Typography>
                                {overdueChartData.length > 0 ? (
                                    <ResponsiveContainer width="100%" height={300}>
                                        {/* @ts-ignore - recharts types incompatible with React 18 */}
                                        <ComposedChart data={overdueChartData}>
                                            <CartesianGrid strokeDasharray="3 3" />
                                            <XAxis dataKey="month" fontSize={12} />
                                            <YAxis
                                                yAxisId="left"
                                                tickFormatter={(v: number) => `R$${(v / 1000).toFixed(0)}k`}
                                                fontSize={12}
                                            />
                                            <YAxis yAxisId="right" orientation="right" fontSize={12} />
                                            <Tooltip formatter={tooltipFormatter} />
                                            <Legend />
                                            <Bar
                                                yAxisId="left"
                                                dataKey="SaldoAberto"
                                                fill={CHART_COLORS[1]}
                                                name="Saldo em Aberto"
                                                radius={[4, 4, 0, 0]}
                                            />
                                            <Line
                                                yAxisId="right"
                                                type="monotone"
                                                dataKey="count"
                                                stroke={CHART_COLORS[0]}
                                                strokeWidth={2}
                                                name="Qtde Parcelas"
                                                dot={{ fill: CHART_COLORS[0] }}
                                            />
                                        </ComposedChart>
                                    </ResponsiveContainer>
                                ) : (
                                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
                                        <Typography color="text.secondary">Sem dados de inadimplência</Typography>
                                    </Box>
                                )}
                            </Paper>
                        </Grid>

                        {/* Chart 2: Previsao */}
                        <Grid item xs={12} md={6}>
                            <Paper elevation={2} sx={{ p: 2 }}>
                                <Typography variant="h6" gutterBottom>Previsão - a partir de hoje</Typography>
                                {forecastChartData.length > 0 ? (
                                    <ResponsiveContainer width="100%" height={300}>
                                        {/* @ts-ignore - recharts types incompatible with React 18 */}
                                        <BarChart data={forecastChartData}>
                                            <CartesianGrid strokeDasharray="3 3" />
                                            <XAxis dataKey="month" fontSize={12} />
                                            <YAxis
                                                tickFormatter={(v: number) => `R$${(v / 1000).toFixed(0)}k`}
                                                fontSize={12}
                                            />
                                            <Tooltip formatter={tooltipFormatter} />
                                            <Legend />
                                            <Bar
                                                dataKey="ValorPrevisto"
                                                fill={CHART_COLORS[0]}
                                                name="Valor Previsto"
                                                radius={[4, 4, 0, 0]}
                                            />
                                        </BarChart>
                                    </ResponsiveContainer>
                                ) : (
                                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
                                        <Typography color="text.secondary">Sem dados de previsão</Typography>
                                    </Box>
                                )}
                            </Paper>
                        </Grid>
                    </Grid>

                    {/* Delinquency Table */}
                    <Paper elevation={2}>
                        <MUIDataTable
                            title="Detalhamento de Inadimplência"
                            data={delinquencyTable}
                            columns={delinquencyColumns}
                            options={delinquencyTableOptions}
                        />
                    </Paper>
                </>
            )}
        </Box>
    );
};

export default Dashboard;
